import { useRef, useState } from 'react';
import { Avatar, Box, Card, IconButton, Typography } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { useRouteLoaderData } from 'react-router-dom';
import { toast } from 'react-toastify';

import ToastMessage from '@/components/toasts/ToastMessage';
import api from '@/services/ApiService';
import files from '@/services/FileService';
import tenant from '@/services/TenantService';

import useAppStore from '@/store/app';

export default function LogoForm() {
  const { client, config } = useRouteLoaderData('auth');
  const { deployment } = client;

  const { logoFileId, setLogoFileId } = useAppStore();

  const [logoFile, setLogoFile] = useState(null);

  const fileInputRef = useRef(null);

  const handleEditClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await uploadLogo(file);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadLogo(file);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) {
      return;
    }
    try {
      const { data: fileObj } = await api.post('/file', {
        filename: `logo/${deployment._id}.webp`,
        size: 1000,
        public: true,
      });

      if (fileObj.acknowledged) {
        const newFileId = fileObj.insertedId;

        await tenant.put(
          `/deployment/${deployment._id}`,
          JSON.stringify({ config: { ...config, logoFileId: newFileId } }),
        );

        await files.post(`/${newFileId}`, { file });

        setLogoFile(file);
        setLogoFileId(newFileId);

        toast.success(
          <ToastMessage title="Erfolg" subtitle="Logo aktualisiert" />,
        );
      }
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle={error?.response?.data?.message || 'Upload fehlgeschlagen'}
        />,
      );
    }
  };

  const deleteLogo = async () => {
    try {
      if (logoFileId) {
        await files.post(`/delete/${logoFileId}`);
      }

      await tenant.put(
        `/deployment/${deployment._id}`,
        JSON.stringify({ config: { ...config, logoFileId: null } }),
      );

      setLogoFile(null);
      setLogoFileId(null);

      toast.success(<ToastMessage title="Erfolg" subtitle="Logo entfernt" />);
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle={error?.response?.data?.message || 'Löschen fehlgeschlagen'}
        />,
      );
    }
  };

  return (
    <Card sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Logo
      </Typography>

      <Box>
        <Box
          sx={{
            position: 'relative',
            height: '100%',
            width: '100%',
            '&:hover .avatar-actions': {
              opacity: 1,
            },
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <Avatar
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '0%',
            }}
            src={
              logoFile
                ? URL.createObjectURL(logoFile)
                : logoFileId
                  ? `${import.meta.env.VITE_FILE_URL}/${logoFileId}`
                  : ''
            }
            imgProps={{ style: { objectFit: 'contain' } }}
          />

          <Box
            className="avatar-actions"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent:
                logoFileId || logoFile ? 'space-around' : 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              opacity: 0,
              transition: 'opacity 0.3s',
            }}
          >
            {logoFileId || logoFile ? (
              <>
                <IconButton onClick={handleEditClick} sx={{ color: 'white' }}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={deleteLogo} sx={{ color: 'white' }}>
                  <DeleteIcon />
                </IconButton>
              </>
            ) : (
              <IconButton onClick={handleEditClick} sx={{ color: 'white' }}>
                <AddIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>
    </Card>
  );
}
